import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import './styles/App.css';
import { BrowserRouter, useLocation, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Main from './components/layout/Main';
import { DashboardProvider } from './contexts/DashboardContext';
import { ColorPaletteProvider } from './contexts/ColorPaletteContext';
import usePageTracking from './hooks/usePageTracking';
import { useUrlSync } from './hooks/useUrlSync';
import { usePersistence } from './hooks/usePersistence';
import { useUserBehaviorTracking } from './hooks/useUserBehaviorTracking';
import { useResearchParticipantFromUrl } from './hooks/useResearchParticipantFromUrl';
import { detectLanguageFromPathname, translate } from './i18n';
import { featureFlags } from './config/featureFlags';
import { isMeidaisaiRoute } from './config/meidaisaiDemo';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { detectAndTrackTVEntry } from './utils/analytics';
import TVFeedbackDialog from './components/TVFeedbackDialog';

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <FeatureFlagsProvider>
          <AppContent />
        </FeatureFlagsProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

const isDev = (import.meta.env.VITE_API_BASE_URL || '').includes('dev');

// Router配下でContextとフックを初期化
function AppContent() {
  // ページトラッキングを有効化
  usePageTracking();
  const location = useLocation();
  const language = detectLanguageFromPathname(location.pathname);
  const isAdminRoute = featureFlags.admin && /^\/(en\/)?admin\/?$/.test(location.pathname);
  const isTvDemoRoute = featureFlags.tvDemo && /^\/tv-demo\/?$/.test(location.pathname);
  const isMeidaisaiDemoRoute = isMeidaisaiRoute(location.pathname);
  const isFullscreenRoute = isAdminRoute || isTvDemoRoute;
  const hideHeader = isFullscreenRoute;

  useEffect(() => {
    detectAndTrackTVEntry();
  }, [location.pathname, location.search]);

  return (
    <DashboardProvider>
      <SyncHooks disabled={isMeidaisaiDemoRoute} />
      <ColorPaletteProvider>
        <div
          className={`app-container${isFullscreenRoute ? ` app-container--${isTvDemoRoute ? 'tv-demo' : 'admin'}` : ''}`}
        >
          {isDev && !hideHeader && !isMeidaisaiDemoRoute && <DevBanner language={language} />}
          {!hideHeader && <Header />}
          <Main />
          {!isFullscreenRoute && <TVFeedbackDialog />}
          {!isFullscreenRoute && <Footer />}
        </div>
      </ColorPaletteProvider>
    </DashboardProvider>
  );
}

function DevBanner({ language }) {
  return (
    <div style={{
      background: 'linear-gradient(90deg, #f59e0b, #d97706)',
      color: '#fff',
      textAlign: 'center',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '4px 0',
      letterSpacing: '0.5px',
      position: 'sticky',
      top: 0,
      zIndex: 9999,
    }}>
      {translate(language, 'app.devBanner')}
      {featureFlags.tvDemo && (
        <>
          {' · '}
          <Link
            to="/tv-demo"
            style={{ color: '#fff', textDecoration: 'underline' }}
          >
            {language === 'en' ? 'TV forecast demo' : 'テレビ向けデモ (/tv-demo)'}
          </Link>
        </>
      )}
    </div>
  );
}

DevBanner.propTypes = {
  language: PropTypes.oneOf(['ja', 'en']).isRequired,
};

// URL同期・Cookie管理・行動トラッキングのフックを適用
function SyncHooksInner() {
  useResearchParticipantFromUrl();
  useUrlSync();
  usePersistence();
  useUserBehaviorTracking();
  return null;
}

function SyncHooks({ disabled = false }) {
  if (disabled) return null;
  return <SyncHooksInner />;
}

export default App;
